import React, { useState } from 'react';
import { 
  Button, 
  Table, 
  Space, 
  Breadcrumb, 
  Card,
  Row,
  Col,
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
  Modal,
  message,
  Tabs,
  Badge,
  Tooltip,
  Divider,
  InputNumber,
  Radio,
  Typography,
  Alert
} from 'antd';
import { 
  SearchOutlined, 
  CheckOutlined, 
  CloseOutlined, 
  EyeOutlined, 
  ExclamationCircleOutlined,
  DownOutlined,
  UpOutlined,
  ReloadOutlined,
  DownloadOutlined,
  LockOutlined,
  UnlockOutlined,
  CreditCardOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Title, Text } = Typography;

// 在文件开头添加CSS样式
const tableStyles = {
  table: {
    fontSize: '14px',
  },
  row: {
    height: '60px', // 增加行高
  },
  tableWrapper: {
    margin: '24px 0',
  },
  tabsWrapper: {
    padding: '0 12px',
  }
};

// 提现申请数据接口
interface WithdrawalRequestData {
  key: string;
  requestId: string;
  employeeName: string;
  department: string;
  amount: number;
  requestTime: string;
  status: 'pending' | 'approved' | 'rejected';
  phone: string;
  processor?: string;
  processTime?: string;
  reason?: string;
}

// 异常订单数据接口
interface AbnormalOrderData {
  key: string;
  orderId: string;
  orderType: string;
  customerName: string;
  employeeName: string;
  amount: number;
  commissionAmount: number;
  orderTime: string;
  status: 'normal' | 'frozen' | 'recovered';
  abnormalReason?: string;
}

// 模拟数据
const withdrawalRequestsData: WithdrawalRequestData[] = [
  {
    key: '1',
    requestId: 'WR20231001001',
    employeeName: '张三',
    department: '客房部',
    amount: 350,
    requestTime: '2023-10-01 14:30:45',
    status: 'pending',
    phone: '13812345678',
  },
  {
    key: '2',
    requestId: 'WR20231002002',
    employeeName: '李四',
    department: '餐饮部',
    amount: 280,
    requestTime: '2023-10-02 09:15:22',
    status: 'approved',
    phone: '13987654321',
    processor: '王经理',
    processTime: '2023-10-02 15:20:35',
    reason: '正常提现，已审核通过',
  },
  {
    key: '3',
    requestId: 'WR20231003003',
    employeeName: '王五',
    department: '客房部',
    amount: 150,
    requestTime: '2023-10-03 16:45:10',
    status: 'rejected',
    phone: '13756781234',
    processor: '李主管',
    processTime: '2023-10-03 18:30:22',
    reason: '提供的银行账户信息有误',
  },
  {
    key: '4',
    requestId: 'WR20231004004',
    employeeName: '赵六',
    department: '前厅部',
    amount: 210,
    requestTime: '2023-10-04 11:20:33',
    status: 'pending',
    phone: '13567891234',
  },
  {
    key: '5',
    requestId: 'WR20231005005',
    employeeName: '钱七',
    department: '餐饮部',
    amount: 320,
    requestTime: '2023-10-05 15:05:18',
    status: 'pending',
    phone: '13678901234',
  },
];

const abnormalOrdersData: AbnormalOrderData[] = [
  {
    key: '1',
    orderId: 'ORD20231001001',
    orderType: '客房预订',
    customerName: '顾客A',
    employeeName: '张三',
    amount: 3600,
    commissionAmount: 180,
    orderTime: '2023-10-01 12:30:45',
    status: 'frozen',
    abnormalReason: '客户投诉服务质量问题',
  },
  {
    key: '2',
    orderId: 'ORD20231002002',
    orderType: '餐饮消费',
    customerName: '顾客B',
    employeeName: '李四',
    amount: 1200,
    commissionAmount: 36,
    orderTime: '2023-10-02 19:15:22',
    status: 'normal',
  },
  {
    key: '3',
    orderId: 'ORD20231003003',
    orderType: '会员充值',
    customerName: '顾客C',
    employeeName: '王五',
    amount: 5000,
    commissionAmount: 100,
    orderTime: '2023-10-03 10:45:10',
    status: 'recovered',
    abnormalReason: '虚假充值交易',
  },
  {
    key: '4',
    orderId: 'ORD20231004004',
    orderType: '客房预订',
    customerName: '顾客D',
    employeeName: '赵六',
    amount: 4800,
    commissionAmount: 240,
    orderTime: '2023-10-04 14:20:33',
    status: 'frozen',
    abnormalReason: '疑似刷单行为',
  },
  {
    key: '5',
    orderId: 'ORD20231005005',
    orderType: '餐饮消费',
    customerName: '顾客E',
    employeeName: '钱七',
    amount: 2500,
    commissionAmount: 75,
    orderTime: '2023-10-05 18:05:18',
    status: 'normal',
  },
];

const CommissionAuditManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [searchCollapsed, setSearchCollapsed] = useState(false);
  const [auditModalVisible, setAuditModalVisible] = useState(false);
  const [auditType, setAuditType] = useState<'approve' | 'reject'>('approve');
  const [selectedRequest, setSelectedRequest] = useState<WithdrawalRequestData | null>(null);
  const [abnormalOrderModalVisible, setAbnormalOrderModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<AbnormalOrderData | null>(null);
  const [abnormalActionType, setAbnormalActionType] = useState<'freeze' | 'recover'>('freeze');

  // 提现申请表格列定义
  const withdrawalColumns: ColumnsType<WithdrawalRequestData> = [
    {
      title: '申请编号',
      dataIndex: 'requestId',
      key: 'requestId',
      width: 150,
    },
    {
      title: '员工姓名',
      dataIndex: 'employeeName',
      key: 'employeeName',
      width: 100,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (phone) => {
        const maskedPhone = phone.substring(0, 3) + '****' + phone.substring(7);
        return (
          <Tooltip title={phone}>
            <span>{maskedPhone}</span>
          </Tooltip>
        );
      },
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department',
      width: 120,
    },
    {
      title: '提现金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount) => `¥${amount.toLocaleString()}`,
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: '申请时间',
      dataIndex: 'requestTime',
      key: 'requestTime',
      width: 180,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <>
          {status === 'pending' && <Badge status="processing" text="待审核" />}
          {status === 'approved' && <Badge status="success" text="已通过" />}
          {status === 'rejected' && <Badge status="error" text="已拒绝" />}
        </>
      ),
    },
    {
      title: '处理人',
      dataIndex: 'processor',
      key: 'processor',
      width: 100,
      render: (processor) => processor || '-',
    },
    {
      title: '处理时间',
      dataIndex: 'processTime',
      key: 'processTime',
      width: 180,
      render: (processTime) => processTime || '-',
    },
    {
      title: '备注',
      dataIndex: 'reason',
      key: 'reason',
      width: 180,
      ellipsis: true,
      render: (reason) => reason ? (
        <Tooltip title={reason}>
          <span>{reason}</span>
        </Tooltip>
      ) : '-',
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 200,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          
          {record.status === 'pending' && (
            <>
              <Button 
                type="link" 
                style={{ padding: '4px 8px', color: 'green', minWidth: 48 }}
                onClick={() => {
                  setSelectedRequest(record);
                  setAuditType('approve');
                  setAuditModalVisible(true);
                }}
              >
                <CheckOutlined /> 通过
              </Button>
              <Button 
                type="link" 
                danger 
                style={{ padding: '4px 8px', minWidth: 48 }}
                onClick={() => {
                  setSelectedRequest(record);
                  setAuditType('reject');
                  setAuditModalVisible(true);
                }}
              >
                <CloseOutlined /> 拒绝
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  // 异常订单表格列定义
  const abnormalOrderColumns: ColumnsType<AbnormalOrderData> = [
    {
      title: '订单编号',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 150,
    },
    {
      title: '订单类型',
      dataIndex: 'orderType',
      key: 'orderType',
      width: 110,
    },
    {
      title: '客户姓名',
      dataIndex: 'customerName',
      key: 'customerName',
      width: 110,
    },
    {
      title: '员工姓名',
      dataIndex: 'employeeName',
      key: 'employeeName',
      width: 110,
    },
    {
      title: '订单金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      render: (amount) => `¥${amount.toLocaleString()}`,
    },
    {
      title: '佣金金额',
      dataIndex: 'commissionAmount',
      key: 'commissionAmount',
      width: 110,
      render: (amount) => `¥${amount.toLocaleString()}`,
    },
    {
      title: '订单时间',
      dataIndex: 'orderTime',
      key: 'orderTime',
      width: 180,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <>
          {status === 'normal' && <Badge status="success" text="正常" />}
          {status === 'frozen' && <Badge status="error" text="已冻结" />}
          {status === 'recovered' && <Badge status="warning" text="已追回" />}
        </>
      ),
    },
    {
      title: '异常原因',
      dataIndex: 'abnormalReason',
      key: 'abnormalReason',
      width: 180,
      ellipsis: true,
      render: (reason) => reason ? (
        <Tooltip title={reason}>
          <span>{reason}</span>
        </Tooltip>
      ) : '-',
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 140,
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EyeOutlined />} style={{ padding: '4px 8px' }}>查看</Button>
          {record.status === 'normal' && (
            <Button 
              type="link" 
              style={{ padding: '4px 8px' }}
              onClick={() => {
                setSelectedOrder(record);
                setAbnormalActionType('freeze');
                setAbnormalOrderModalVisible(true);
              }}
            >
              <LockOutlined /> 冻结
            </Button>
          )}
          {record.status === 'frozen' && (
            <Button 
              type="link" 
              style={{ padding: '4px 8px', color: 'orange' }}
              onClick={() => {
                setSelectedOrder(record);
                setAbnormalActionType('recover');
                setAbnormalOrderModalVisible(true);
              }}
            >
              <UnlockOutlined /> 追回
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleSearch = () => {
    const formValues = form.getFieldsValue();
    console.log('搜索条件:', formValues);
    message.success('搜索完成');
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleAuditSubmit = () => {
    if (!selectedRequest) return;
    
    // 验证提现金额是否超过500
    if (selectedRequest.amount > 500) {
      message.error('提现金额不能超过500元');
      return;
    }
    
    const action = auditType === 'approve' ? '审核通过' : '拒绝';
    message.success(`已${action}提现申请: ${selectedRequest.requestId}`);
    setAuditModalVisible(false);
  };

  const handleAbnormalOrderAction = () => {
    const action = abnormalActionType === 'freeze' ? '冻结' : '追回';
    message.success(`已${action}订单佣金: ${selectedOrder?.orderId}`);
    setAbnormalOrderModalVisible(false);
  };

  return (
    <div className="commission-audit-management">
      <Breadcrumb 
        style={{ marginBottom: 24 }} 
        items={[
          { title: '首页' },
          { title: '提现记录' },
        ]}
      />
      
      <Card 
        style={{ marginBottom: 24 }}
        bordered={false}
        className="search-form-card"
      >
        <Form
          form={form}
          layout="horizontal"
          onFinish={handleSearch}
        >
          <Row gutter={[32, 16]} style={{ display: searchCollapsed ? 'none' : 'flex' }}>
            <Col span={6}>
              <Form.Item label="员工姓名" name="employeeName">
                <Input placeholder="请输入" allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="申请编号" name="requestId">
                <Input placeholder="请输入" allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="状态" name="status">
                <Select
                  placeholder="请选择"
                  allowClear
                  options={[
                    { value: 'pending', label: '待审核' },
                    { value: 'approved', label: '已通过' },
                    { value: 'rejected', label: '已拒绝' },
                  ]}
                  suffixIcon={<DownOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="申请时间" name="requestTime">
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            marginTop: 24,
            gap: 12 
          }}>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              icon={<SearchOutlined />}
            >
              查询
            </Button>
            <Button 
              type="link"
              onClick={() => setSearchCollapsed(!searchCollapsed)}
            >
              {searchCollapsed ? '展开' : '收起'} {searchCollapsed ? <DownOutlined /> : <UpOutlined />}
            </Button>
          </div>
        </Form>
      </Card>

      <Card bordered={false}>
        <Tabs defaultActiveKey="1" style={tableStyles.tabsWrapper}>
          <TabPane tab="提现申请审核" key="1">
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Radio.Group defaultValue="all" style={{ marginRight: 16 }}>
                  <Radio.Button value="all">全部</Radio.Button>
                  <Radio.Button value="pending">待审核</Radio.Button>
                  <Radio.Button value="approved">已通过</Radio.Button>
                  <Radio.Button value="rejected">已拒绝</Radio.Button>
                </Radio.Group>
              </div>
              <Space size="middle">
                <Button icon={<ReloadOutlined />}>刷新</Button>
                <Button icon={<DownloadOutlined />}>导出</Button>
              </Space>
            </div>

            <div style={tableStyles.tableWrapper}>
              <Table 
                columns={withdrawalColumns} 
                dataSource={withdrawalRequestsData}
                rowKey="key"
                style={tableStyles.table}
                onRow={() => ({ style: tableStyles.row })}
                pagination={{
                  pageSize: 5,
                  showTotal: (total) => `共 ${total} 条记录`,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  size: 'default',
                }}
                scroll={{ x: 'max-content' }}
              />
            </div>
          </TabPane>
          
          <TabPane tab="异常订单处理" key="2">
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Radio.Group defaultValue="all" style={{ marginRight: 16 }}>
                  <Radio.Button value="all">全部订单</Radio.Button>
                  <Radio.Button value="normal">正常订单</Radio.Button>
                  <Radio.Button value="frozen">已冻结</Radio.Button>
                  <Radio.Button value="recovered">已追回</Radio.Button>
                </Radio.Group>
              </div>
              <Space size="middle">
                <Button icon={<ReloadOutlined />}>刷新</Button>
                <Button icon={<DownloadOutlined />}>导出</Button>
              </Space>
            </div>

            <div style={tableStyles.tableWrapper}>
              <Table 
                columns={abnormalOrderColumns} 
                dataSource={abnormalOrdersData}
                rowKey="key"
                style={tableStyles.table}
                onRow={() => ({ style: tableStyles.row })}
                pagination={{
                  pageSize: 5,
                  showTotal: (total) => `共 ${total} 条记录`,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  size: 'default',
                }}
                scroll={{ x: 'max-content' }}
              />
            </div>
          </TabPane>
        </Tabs>
      </Card>
      
      {/* 提现申请审核弹窗 */}
      <Modal
        title={auditType === 'approve' ? '审核通过提现申请' : '拒绝提现申请'}
        open={auditModalVisible}
        onCancel={() => setAuditModalVisible(false)}
        onOk={handleAuditSubmit}
        width={650}
      >
        <div style={{ marginBottom: 24 }}>
          {selectedRequest && selectedRequest.amount > 500 && (
            <div style={{ marginBottom: 24 }}>
              <Alert
                message="提现金额超出限制"
                description="提现金额不能超过500元，请联系员工修改申请金额。"
                type="error"
                showIcon
              />
            </div>
          )}
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <div className="info-item">
                <Text type="secondary">申请编号：</Text>
                <Text strong>{selectedRequest?.requestId}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div className="info-item">
                <Text type="secondary">员工姓名：</Text>
                <Text strong>{selectedRequest?.employeeName}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div className="info-item">
                <Text type="secondary">手机号：</Text>
                <Text>{selectedRequest?.phone}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div className="info-item">
                <Text type="secondary">部门：</Text>
                <Text>{selectedRequest?.department}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div className="info-item">
                <Text type="secondary">提现金额：</Text>
                <Text strong style={{ color: selectedRequest && selectedRequest.amount > 500 ? '#ff4d4f' : undefined, fontSize: '16px' }}>
                  ¥{selectedRequest?.amount.toLocaleString()}
                  {selectedRequest && selectedRequest.amount > 500 && ' (超出限制)'}
                </Text>
              </div>
            </Col>
            <Col span={12}>
              <div className="info-item">
                <Text type="secondary">申请时间：</Text>
                <Text>{selectedRequest?.requestTime}</Text>
              </div>
            </Col>
          </Row>
        </div>
        
        <Divider style={{ margin: '12px 0 24px' }} />
        
        {auditType === 'approve' ? (
          <Form layout="vertical">
            <Form.Item 
              label="审核备注"
              name="approveReason"
            >
              <TextArea rows={4} placeholder="请输入审核备注（选填）" />
            </Form.Item>
          </Form>
        ) : (
          <Form layout="vertical">
            <Form.Item 
              label="拒绝原因" 
              name="rejectReason"
              rules={[{ required: true, message: '请输入拒绝原因' }]}
            >
              <TextArea rows={4} placeholder="请输入拒绝原因" />
            </Form.Item>
          </Form>
        )}
      </Modal>
      
      {/* 异常订单处理弹窗 */}
      <Modal
        title={
          abnormalActionType === 'freeze' 
            ? '冻结订单佣金' 
            : '追回订单佣金'
        }
        open={abnormalOrderModalVisible}
        onCancel={() => setAbnormalOrderModalVisible(false)}
        onOk={handleAbnormalOrderAction}
        width={650}
      >
        <div style={{ marginBottom: 24 }}>
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <div className="info-item">
                <Text type="secondary">订单编号：</Text>
                <Text strong>{selectedOrder?.orderId}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div className="info-item">
                <Text type="secondary">订单类型：</Text>
                <Text>{selectedOrder?.orderType}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div className="info-item">
                <Text type="secondary">客户姓名：</Text>
                <Text>{selectedOrder?.customerName}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div className="info-item">
                <Text type="secondary">员工姓名：</Text>
                <Text>{selectedOrder?.employeeName}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div className="info-item">
                <Text type="secondary">订单金额：</Text>
                <Text>¥{selectedOrder?.amount.toLocaleString()}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div className="info-item">
                <Text type="secondary">佣金金额：</Text>
                <Text strong style={{ color: '#f5222d', fontSize: '16px' }}>
                  ¥{selectedOrder?.commissionAmount.toLocaleString()}
                </Text>
              </div>
            </Col>
          </Row>
        </div>
        
        <Divider style={{ margin: '12px 0 24px' }} />
        
        <Form layout="vertical">
          {abnormalActionType === 'freeze' ? (
            <>
              <Form.Item 
                label="冻结原因" 
                name="freezeReason"
                rules={[{ required: true, message: '请输入冻结原因' }]}
              >
                <TextArea rows={4} placeholder="请输入冻结原因" />
              </Form.Item>
              <Form.Item 
                label="冻结期限" 
                name="freezePeriod"
                rules={[{ required: true, message: '请选择冻结期限' }]}
              >
                <Select
                  placeholder="请选择冻结期限"
                  options={[
                    { value: '7days', label: '7天' },
                    { value: '15days', label: '15天' },
                    { value: '30days', label: '30天' },
                    { value: 'permanent', label: '永久冻结' },
                  ]}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item 
                label="追回原因" 
                name="recoverReason"
                rules={[{ required: true, message: '请输入追回原因' }]}
              >
                <TextArea rows={4} placeholder="请输入追回原因" />
              </Form.Item>
              <Form.Item 
                label="追回金额" 
                name="recoverAmount"
                rules={[{ required: true, message: '请输入追回金额' }]}
                initialValue={selectedOrder?.commissionAmount}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  addonBefore="¥" 
                  min={0} 
                  max={selectedOrder?.commissionAmount}
                  precision={2}
                />
              </Form.Item>
              <Form.Item 
                label="是否通知员工" 
                name="notifyEmployee"
                initialValue={true}
              >
                <Radio.Group>
                  <Radio value={true}>是</Radio>
                  <Radio value={false}>否</Radio>
                </Radio.Group>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default CommissionAuditManagement; 